import { Graph, layout } from '@dagrejs/dagre';
import type {
  CloudEdge,
  CloudEdgeKind,
  CloudGraph,
  CloudNode,
  CloudNodeType,
  SeedReason,
} from '@ai-secure-twin/shared';

/**
 * How the cloud graph is drawn: which nodes and edges are shown, how each type is labelled and
 * grouped, and where dagre puts them. Pure, so the same graph and filters give the same picture.
 */

export type NodeCategory = 'edge' | 'compute' | 'data' | 'identity' | 'external';

export const NODE_TYPE: Record<
  CloudNodeType,
  { label: string; short: string; category: NodeCategory }
> = {
  DOMAIN: { label: 'Domain', short: 'DNS', category: 'edge' },
  CLOUDFRONT_DISTRIBUTION: { label: 'CloudFront', short: 'CDN', category: 'edge' },
  WAF_WEB_ACL: { label: 'WAF web ACL', short: 'WAF', category: 'edge' },
  API_GATEWAY_HTTP: { label: 'HTTP API', short: 'API', category: 'edge' },
  API_GATEWAY_REST: { label: 'REST API', short: 'API', category: 'edge' },
  LOAD_BALANCER: { label: 'Load balancer', short: 'ELB', category: 'edge' },
  COGNITO_USER_POOL: { label: 'Cognito user pool', short: 'IdP', category: 'identity' },
  IAM_ROLE: { label: 'IAM role', short: 'IAM', category: 'identity' },
  TARGET_GROUP: { label: 'Target group', short: 'TG', category: 'compute' },
  ECS_SERVICE: { label: 'ECS service', short: 'ECS', category: 'compute' },
  LAMBDA_FUNCTION: { label: 'Lambda function', short: 'λ', category: 'compute' },
  EVENTBRIDGE_RULE: { label: 'EventBridge rule', short: 'EVT', category: 'compute' },
  STATE_MACHINE: { label: 'Step Functions', short: 'SFN', category: 'compute' },
  SQS_QUEUE: { label: 'SQS queue', short: 'SQS', category: 'data' },
  SNS_TOPIC: { label: 'SNS topic', short: 'SNS', category: 'data' },
  DYNAMODB_TABLE: { label: 'DynamoDB table', short: 'DDB', category: 'data' },
  RDS_DATABASE: { label: 'RDS database', short: 'RDS', category: 'data' },
  ELASTICACHE_CLUSTER: { label: 'ElastiCache', short: 'MEM', category: 'data' },
  S3_BUCKET: { label: 'S3 bucket', short: 'S3', category: 'data' },
  SECRET: { label: 'Secret', short: 'KEY', category: 'data' },
  EXTERNAL: { label: 'External host', short: 'EXT', category: 'external' },
};

export const EDGE_KIND: Record<CloudEdgeKind, { label: string; dashed: boolean }> = {
  ROUTES_TO: { label: 'routes to', dashed: false },
  PROTECTED_BY: { label: 'protected by', dashed: true },
  AUTHENTICATES_WITH: { label: 'authenticates with', dashed: true },
  TRIGGERS: { label: 'triggers', dashed: false },
  RUNS_AS: { label: 'runs as', dashed: true },
  CAN_ACCESS: { label: 'may access (IAM)', dashed: true },
  REFERENCES: { label: 'references', dashed: true },
  NETWORK_REACHABLE: { label: 'can reach (network)', dashed: true },
};

export interface GraphFilters {
  /** Only resources the application's hosts lead to. */
  applicationOnly: boolean;
  /** IAM roles and their RUNS_AS / CAN_ACCESS edges — the blast-radius layer, off by default. */
  showPermissions: boolean;
}

const PERMISSION_KINDS: ReadonlySet<CloudEdgeKind> = new Set(['RUNS_AS', 'CAN_ACCESS']);

export function visibleGraph(
  graph: CloudGraph,
  seed: readonly SeedReason[],
  filters: GraphFilters,
): { nodes: CloudNode[]; edges: CloudEdge[] } {
  const inSeed = new Set(seed.map((s) => s.nodeId));
  const nodes = graph.nodes.filter(
    (n) =>
      (!filters.applicationOnly || inSeed.has(n.id)) &&
      (filters.showPermissions || n.type !== 'IAM_ROLE'),
  );
  const ids = new Set(nodes.map((n) => n.id));
  const edges = graph.edges.filter(
    (e) =>
      ids.has(e.from) &&
      ids.has(e.to) &&
      (filters.showPermissions || !PERMISSION_KINDS.has(e.kind)),
  );
  // In the whole-account view, a resource connected to nothing visible is noise: drop it.
  if (filters.applicationOnly) return { nodes, edges };
  const linked = new Set(edges.flatMap((e) => [e.from, e.to]));
  return { nodes: nodes.filter((n) => linked.has(n.id) || inSeed.has(n.id)), edges };
}

export const CLOUD_NODE_WIDTH = 200;
export const CLOUD_NODE_HEIGHT = 64;

/** Left to right: entry points on the left, data on the right. */
export function layoutCloudGraph(
  nodes: readonly CloudNode[],
  edges: readonly CloudEdge[],
): Map<string, { x: number; y: number }> {
  const g = new Graph();
  g.setGraph({ rankdir: 'LR', nodesep: 24, ranksep: 90, marginx: 16, marginy: 16 });
  g.setDefaultEdgeLabel(() => ({}));
  for (const n of [...nodes].sort((a, b) => a.id.localeCompare(b.id))) {
    g.setNode(n.id, { width: CLOUD_NODE_WIDTH, height: CLOUD_NODE_HEIGHT });
  }
  for (const e of edges) {
    // Permission edges describe reach, not flow; they should not pull the layout around.
    g.setEdge(e.from, e.to, { weight: PERMISSION_KINDS.has(e.kind) ? 0 : 1, minlen: 1 });
  }
  layout(g);
  const positions = new Map<string, { x: number; y: number }>();
  for (const id of g.nodes()) {
    const p = g.node(id);
    positions.set(id, { x: p.x - CLOUD_NODE_WIDTH / 2, y: p.y - CLOUD_NODE_HEIGHT / 2 });
  }
  return positions;
}
